import { useCallback, useMemo } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";

import DateStamp from "@components/dates/DateStamp";
import useMyClient from "@hooks/useMyClient";
import dataTableStyles from "@theme/DataTable.module.css";
import { PackageGrantFragment as PackageGrant } from "@typing/Generated";
import { packageGrantDescription, sortByTimestamp } from "@utils/modelUtils";

type Props = {
  packageGrants: PackageGrant[] | undefined;
};

const PackageGrantTable = ({ packageGrants }: Props) => {
  const myClient = useMyClient();
  const intl = useIntl();

  const purchaserContentFor = useCallback(
    (packageGrant: PackageGrant) => {
      if (packageGrant.purchaser) {
        if (packageGrant.purchaser.__typename === "Client") {
          if (packageGrant.purchaser.id === myClient?.id) {
            return <FormattedMessage id="pages.client.account.timeGrantExplanations.youPurchase" />;
          }
          const client = packageGrant.purchaser;
          return (
            <FormattedMessage
              id="pages.client.account.timeGrantExplanations.clientPurchase"
              values={{ firstName: client.fullName }}
            />
          );
        }
        const employer = packageGrant.purchaser;
        return (
          <FormattedMessage
            id="pages.client.account.timeGrantExplanations.employerPurchase"
            values={{ name: employer.name }}
          />
        );
      }

      return <FormattedMessage id="dictionary.grayce" />;
    },
    [myClient?.id]
  );

  const sortedPackageGrants = useMemo(
    () =>
      packageGrants &&
      sortByTimestamp<PackageGrant>(packageGrants, grant => grant.eligibilityDeterminedAt ?? grant.createdAt).reverse(),
    [packageGrants]
  );

  return (
    <div className={dataTableStyles.scrollX}>
      <table className={dataTableStyles.dataTable}>
        <thead>
          <tr>
            <th>
              <FormattedMessage id="models.packageGrant.tableHeadings.purchasedBy" />
            </th>
            <th>
              <FormattedMessage id="models.packageGrant.tableHeadings.purchaseDate" />
            </th>
            <th>
              <FormattedMessage id="models.packageGrant.tableHeadings.details" />
            </th>
            <th>
              <FormattedMessage id="models.packageGrant.tableHeadings.timeRemaining" />
            </th>
            <th>
              <FormattedMessage id="models.packageGrant.tableHeadings.expirationDate" />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedPackageGrants?.map(packageGrant => (
            <tr key={packageGrant.id}>
              <td>{purchaserContentFor(packageGrant)}</td>
              <td>
                <DateStamp showWeekday={false} value={packageGrant.createdAt} />
              </td>
              <td>
                {packageGrantDescription({
                  hasCarePlanning: packageGrant.hasCarePlanning,
                  hasUnlimitedMinutes: packageGrant.hasUnlimitedMinutes,
                  intl,
                  minutes: packageGrant.minutes
                })}
              </td>
              <td>
                {packageGrant.expired ? (
                  <FormattedNumber value={0} />
                ) : (
                  <>
                    {packageGrantDescription({
                      hasCarePlanning: packageGrant.hasCarePlanning && !packageGrant.carePlanningCompleted,
                      hasUnlimitedMinutes: packageGrant.hasUnlimitedMinutes,
                      intl,
                      minutes: packageGrant.minutesRemaining
                    })}
                  </>
                )}
              </td>
              <td>{packageGrant.expiresAt && <DateStamp showWeekday={false} value={packageGrant.expiresAt} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PackageGrantTable;
